<template>
  <div class="space-y-2">
    <label class="text-base text-[#000] mb-0">{{ t('propertyReport.form.homeOwnerAddress') }} <span class="text-[#762123] italic">*</span></label>
    <div class="relative">
      <input ref="autocompleteEl" type="text" :placeholder="placeholder" class="w-full bg-[#fff] border rounded px-2 py-2.5 pr-10 text-base" @input="onInput" @keydown.enter="onEnterKey" />
      <i class="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-[#171717] text-xl pointer-events-none"></i>
    </div>
    <div ref="mapEl" class="h-80 mt-4 rounded border" :class="compactMap ? 'w-full lg:w-1/2' : 'w-full'"></div>
    <p v-if="touched && !valid" class="text-xs text-red-600">{{ t('propertyReport.form.invalidAddress') }}</p>
  </div>
</template>
<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import { loadGoogleMaps } from '../utilities/maps/loadGoogleMaps'
import { useI18n } from 'vue-i18n'
import { getLanguage } from '@/utilities/cookies';
 
const { t, locale } = useI18n()
locale.value = getLanguage();
 
const emit = defineEmits(['address-validity-changed','address-changed','search-triggered'])
const props = defineProps({
  placeholder: { type: String, default: '' },
  compactMap: { type: Boolean, default: false }
})
 
const compactMap = computed(() => props.compactMap)
const placeholder = computed(() => props.placeholder || t('homeownerInsights.enterHomeAddress'))
 
const touched = ref(false)
const addr = reactive({ shipToAddress1:'', shipToAddressCity:'', shipToAddressState:'', shipToAddressZipCode:'' })
const mapAddress = reactive({ streetNumber:'', streetAddress:'', city:'', state:'', zipCode:'' })
const mapEl = ref(null)
const autocompleteEl = ref(null)
const gAutocomplete = ref(null)
const gMap = ref(null)
const gMarker = ref(null)
 
 
const valid = ref(false)
watch(()=>[addr.shipToAddress1,addr.shipToAddressCity,addr.shipToAddressState,addr.shipToAddressZipCode],()=>{
  valid.value = !!addr.shipToAddress1 && !!addr.shipToAddressCity && !!addr.shipToAddressState && !!addr.shipToAddressZipCode && /^([0-9]{5}(-[0-9]{4})?)$/.test(addr.shipToAddressZipCode)
  emit('address-validity-changed',{isValid:valid.value,address:mapAddress})
})
 
function onInput(){
  touched.value = true
  if(autocompleteEl.value?.value===''){ clearAddress(); }
}
 
function onEnterKey(){
  emit('search-triggered')
}
 
function clearAddress(){
  Object.assign(addr,{shipToAddress1:'',shipToAddressCity:'',shipToAddressState:'',shipToAddressZipCode:''})
  Object.assign(mapAddress,{streetNumber:'',streetAddress:'',city:'',state:'',zipCode:''})
 
  if(autocompleteEl.value) {
    autocompleteEl.value.value = ''
  }
 
  touched.value = false
  if(gMarker.value){ gMarker.value.setVisible(false) }
  emit('address-validity-changed',{isValid:false,address:mapAddress});
  emit('address-changed',{isValid:false,address:mapAddress});
}
 
async function initMaps() {
  try {
    if (!mapEl.value || !autocompleteEl.value) return;
 
    // If Maps is already loaded but without Places, remove the bad script and reset
    if (window.google && window.google.maps && !window.google.maps.places) {
      console.warn('Google Maps loaded without Places. Removing bad script and resetting...');
      const badScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (badScript) badScript.remove();
      delete window.google.maps;
    }
 
    // Load Maps with Places library
    await loadGoogleMaps();
 
    // Guard: ensure Autocomplete is available
    if (!window.google?.maps?.places?.Autocomplete) {
      console.error('Google Maps Places Autocomplete is not available.');
      return;
    }
 
    // Initialize map
    gMap.value = new window.google.maps.Map(mapEl.value, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeId: window.google.maps.MapTypeId.HYBRID
    });
 
    // Initialize marker
    gMarker.value = new window.google.maps.Marker({
      map: gMap.value,
      anchorPoint: new window.google.maps.Point(0, -29)
    });
 
    // Initialize autocomplete
    gAutocomplete.value = new window.google.maps.places.Autocomplete(autocompleteEl.value, {
      types: ['address'],
      fields: ['address_component', 'geometry'],
      componentRestrictions: { country: 'us' }
    });
 
    gAutocomplete.value.addListener('place_changed', setAddress);
  } catch (error) {
    console.error('Error initializing Google Maps in AddressLookup:', error);
  }
}
 
 
 
function setAddress(){
  const place = gAutocomplete.value.getPlace()
  if(!place) return
  if(place.geometry){ gMap.value.setCenter(place.geometry.location); gMap.value.setZoom(18); gMarker.value.setPosition(place.geometry.location); gMarker.value.setVisible(true); }
  if(place.address_components){ processAddressComponents(place.address_components) }
  emit('address-changed',{isValid:valid.value,address:mapAddress})
}
function processAddressComponents(components){
  const get = (type)=>{ const c = components.find(ac=>ac.types.includes(type)); return c ? c.short_name : '' }
  mapAddress.streetNumber = get('street_number')
  mapAddress.streetAddress = get('route')
  mapAddress.city = get('locality') || get('administrative_area_level_3')
  mapAddress.state = get('administrative_area_level_1')
  mapAddress.zipCode = get('postal_code')
  addr.shipToAddress1 = mapAddress.streetNumber? `${mapAddress.streetNumber} ${mapAddress.streetAddress}`: mapAddress.streetAddress
  addr.shipToAddressCity = mapAddress.city
  addr.shipToAddressState = mapAddress.state
  addr.shipToAddressZipCode = mapAddress.zipCode
}
 
defineExpose({
  clearAddress
})
 
// Suggestions live under <body>, so inside a modal they drift on inner scroll; re-anchor them to the focused input.
const stickSuggestionsToInput = () => requestAnimationFrame(() => {
  const input = autocompleteEl.value
  const dropdown = [...document.querySelectorAll('.pac-container')].find(el => el.getClientRects().length)
  if (!dropdown || document.activeElement !== input || !input.closest('.fixed')) return
  const { bottom, left } = input.getBoundingClientRect()
  dropdown.style.top = `${bottom + window.scrollY}px`
  dropdown.style.left = `${left + window.scrollX}px`
})
 
onMounted(() => {
  nextTick(() => {
    initMaps();
  });
  window.addEventListener('scroll', stickSuggestionsToInput, true)
})
 
onBeforeUnmount(() => window.removeEventListener('scroll', stickSuggestionsToInput, true))
</script>
 
<style>
.pac-container {
  z-index: 99999 !important;
}
</style>
